import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { Spinner, Nav, Button } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroller';
import {
  getOrderContactSettings,
  getOrderRequests,
} from '../../clients/order_request_client';
import { getProjectSelectList } from '../../clients/project_client';
import { getUserSelectList } from '../../clients/user_client';
import OrderRequestsList from '../../components/OrderRequest/OrderRequestsList';
import OrderRequestFilterComponent from '../../components/OrderRequest/OrderRequestFilterComponent';
import GenericSearchFilter from '../../components/Generic/GenericSearchFilter';
import DropdownSelect from '../../components/Generic/DropdownSelect';
import GlobalOrderContactModal from '../../components/OrderRequest/GlobalOrderContactModal';
import OrderRequestAllSuppliersQuoteButton from '../../components/OrderRequest/OrderRequestAllSuppliersQuoteButton';
import { showToast } from '../../config_and_helpers/helpers';
import {
  logInfo,
  logListSearchError,
  logListSearchEvent,
} from '../../config_and_helpers/logger';
import { mergeOrderRequestHistoryLists } from '../../utils/orderRequestHistory';
/** Shared list-search helpers (partial-query reset, cancel, stale next-page). */
import {
  abortListSearchController,
  getNextPageUrlFilterMismatch,
  isPartialListSearchQuery,
  isRunnableListSearchQuery,
  resetPaginationForPartialSearch,
  INFINITE_SCROLL_THRESHOLD_PX,
} from '../../utils/listSearchRequest';
import { findUserOption } from '../../config_and_helpers/userDropdownOptions';
import GlassWorkspaceShell from '../../components/Glass/GlassWorkspaceShell';

/** @param {string} localDatetime - `datetime-local` value */
const toIsoDatetimeParam = localDatetime => {
  if (!localDatetime?.trim()) return '';
  const d = new Date(localDatetime);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
};

/**
 * Client-side search fallback until backend `search` param is implemented.
 *
 * @param {Array<Object>} rows
 * @param {string} searchInput
 * @returns {Array<Object>}
 */
export const filterOrderRequestsClientSide = (rows, searchInput) => {
  const q = String(searchInput ?? '').trim().toLowerCase();
  if (q.length < 2) return rows;
  return (Array.isArray(rows) ? rows : []).filter(row => {
    const ref = String(row.request_ref ?? '').toLowerCase();
    const requester = String(row.requested_by_name ?? '').toLowerCase();
    const projectName = String(row.project_name ?? '').toLowerCase();
    const products = (row.product_names || []).some(name =>
      String(name).toLowerCase().includes(q)
    );
    return (
      ref.includes(q) ||
      requester.includes(q) ||
      projectName.includes(q) ||
      products
    );
  });
};

const DEFAULT_FILTERS = {
  status: 'REQUESTED',
  productId: '',
  createdAfter: '',
  createdBefore: '',
};

const HISTORY_STATUSES = ['FULFILLED', 'CANCELLED'];
const EMPTY_HISTORY_CURSORS = { FULFILLED: null, CANCELLED: null };

/** Subtitle for the order-requests list empty state (matches QuotesList pattern). */
function getOrderRequestsEmptyMessage({
  searchInput,
  projectId,
  requestedById,
  statusFilter,
  productId,
  createdAfter,
  createdBefore,
  listMode = 'open',
}) {
  if (searchInput.length >= 2) {
    return `No requests match your search for "${searchInput}"`;
  }
  const hasFilters =
    projectId ||
    requestedById ||
    (listMode === 'open' && statusFilter !== DEFAULT_FILTERS.status) ||
    productId ||
    createdAfter ||
    createdBefore;
  if (hasFilters) {
    return 'No order requests match the current filters';
  }
  if (listMode === 'history') {
    return 'No closed order requests yet';
  }
  return 'No order requests available';
}

/**
 * Procurement inbox for internal order requests (inventory → procurement).
 */
const OrderRequestsPage = () => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const isMountedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const debounceTimeoutRef = useRef(null);
  const currentRequestRef = useRef(null);
  const listModeRef = useRef('open');
  const historyNextRef = useRef({ ...EMPTY_HISTORY_CURSORS });

  const [requests, setRequests] = useState([]);
  const [listMode, setListMode] = useState('open');
  const [searchInput, setSearchInput] = useState('');
  const [project, setProject] = useState('');
  const [requestedBy, setRequestedBy] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [projectOptions, setProjectOptions] = useState([]);
  const [userSelectList, setUserSelectList] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showGlobalContactModal, setShowGlobalContactModal] = useState(false);
  const [globalContactSettings, setGlobalContactSettings] = useState(null);
  const [isLoadingGlobalContact, setIsLoadingGlobalContact] = useState(false);
  const [globalContactLoadError, setGlobalContactLoadError] = useState('');
  const hasGlobalContactSettings =
    !!globalContactSettings?.contact_name && !!globalContactSettings?.contact_email;

  let globalContactDisabledReason = '';
  if (isLoadingGlobalContact) {
    globalContactDisabledReason = 'Loading lab contact settings...';
  } else if (globalContactLoadError) {
    globalContactDisabledReason =
      'Quote requests are disabled because lab contact settings could not be loaded.';
  } else if (!hasGlobalContactSettings) {
    globalContactDisabledReason =
      'Quote requests are disabled until a lab contact is configured.';
  }

  const fetchRequests = useCallback(
    (
      {
        searchValue = '',
        projectId = '',
        requestedById = '',
        statusFilter = 'REQUESTED',
        productId = '',
        createdAfter = '',
        createdBefore = '',
        nextPage = null,
        listMode: listModeArg,
      } = {},
      newSearch = false
    ) => {
      if (!token) return;
      if (isLoadingRef.current && !newSearch) return;

      const trigger = newSearch ? 'search-or-filter' : 'scroll-or-refresh';
      const params = {
        searchInput: searchValue.length >= 2 ? searchValue : '',
        project: projectId || undefined,
        requestedBy: requestedById || undefined,
        status: statusFilter || undefined,
        productId: productId || undefined,
        createdAfter: toIsoDatetimeParam(createdAfter) || undefined,
        createdBefore: toIsoDatetimeParam(createdBefore) || undefined,
        nextPage,
      };

      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }

      isLoadingRef.current = true;
      setIsLoading(true);
      if (newSearch) {
        setIsSearching(true);
        setRequests([]);
        setNextPageUrl(null);
        setHasMore(true);
        hasMoreRef.current = true;
      }

      const controller = new AbortController();
      currentRequestRef.current = controller;
      const mode = listModeArg || listModeRef.current;

      if (mode === 'history') {
        const statuses = newSearch
          ? HISTORY_STATUSES
          : HISTORY_STATUSES.filter(status => historyNextRef.current[status]);
        if (!statuses.length) {
          isLoadingRef.current = false;
          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
          setHasMore(false);
          hasMoreRef.current = false;
          return;
        }
        Promise.all(
          statuses.map(status =>
            getOrderRequests(token, null, {
              searchInput: params.searchInput,
              project: params.project,
              requestedBy: params.requestedBy,
              status,
              nextPage: newSearch ? null : historyNextRef.current[status],
              signal: controller.signal,
              updateState: false,
            })
          )
        )
          .then(responses => {
            if (!isMountedRef.current || currentRequestRef.current !== controller) {
              return;
            }
            if (responses.some(response => response?.canceled)) {
              return;
            }
            const nextCursors = newSearch
              ? { ...EMPTY_HISTORY_CURSORS }
              : { ...historyNextRef.current };
            responses.forEach((response, index) => {
              if (response?.success) {
                nextCursors[statuses[index]] = response.nextPage || null;
              }
            });
            historyNextRef.current = nextCursors;
            const incoming = responses
              .filter(response => response?.success)
              .flatMap(response => response.results || []);
            setRequests(prev =>
              mergeOrderRequestHistoryLists(newSearch ? [] : prev, incoming)
            );
            const more = Boolean(nextCursors.FULFILLED || nextCursors.CANCELLED);
            setHasMore(more);
            hasMoreRef.current = more;
            setNextPageUrl(more ? 'history' : null);
            const counts = {};
            responses.forEach((response, index) => {
              counts[statuses[index]] = response?.results?.length ?? 0;
            });
            logInfo('OrderRequestsPage', 'history lists loaded', {
              fulfilledCount: counts.FULFILLED ?? null,
              cancelledCount: counts.CANCELLED ?? null,
              mergedIncoming: incoming.length,
              append: !newSearch,
            });
            if (responses.every(response => response && !response.success)) {
              showToast(
                'Failed to load order requests',
                'error',
                'top-center',
                3000
              );
            }
          })
          .finally(() => {
            if (currentRequestRef.current !== controller || !isMountedRef.current) {
              return;
            }
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
          });
        return;
      }

      getOrderRequests(token, setRequests, { ...params, signal: controller.signal })
        .then(response => {
          if (!isMountedRef.current) return;
          if (currentRequestRef.current !== controller) {
            return;
          }

          if (response?.canceled) {
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            return;
          }

          if (response?.success) {
            setNextPageUrl(response.reachedEnd ? null : response.nextPage);
            setHasMore(!response.reachedEnd);
            hasMoreRef.current = !response.reachedEnd;
          } else {
            showToast(
              'Failed to load order requests',
              'error',
              'top-center',
              3000
            );
          }
          logListSearchEvent(
            'order-requests',
            response?.success ? 'success' : 'failure',
            { trigger, params }
          );
        })
        .catch(err => {
          if (currentRequestRef.current !== controller) {
            return;
          }
          if (
            err?.name === 'AbortError' ||
            err?.code === 'ERR_CANCELED'
          ) {
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            return;
          }

          if (!isMountedRef.current) return;
          logListSearchError('order-requests', trigger, err, { params });
        })
        .finally(() => {
          if (currentRequestRef.current !== controller) {
            return;
          }
          if (!isMountedRef.current) return;
          isLoadingRef.current = false;
          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
        });
    },
    [token]
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }
    };
  }, []);

  useEffect(() => {
    if (token) {
      getProjectSelectList(token, setProjectOptions);
      getUserSelectList(token, setUserSelectList);
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const loadContact = async () => {
      setIsLoadingGlobalContact(true);
      setGlobalContactLoadError('');
      const response = await getOrderContactSettings(token);
      setIsLoadingGlobalContact(false);
      if (response?.success) {
        setGlobalContactSettings(response.data);
      } else {
        setGlobalContactSettings(null);
        setGlobalContactLoadError(
          response?.messages?.[0] ||
            'Lab contact settings could not be loaded.'
        );
      }
    };
    loadContact();
  }, [token]);

  useEffect(() => {
    fetchRequests(
      {
        searchValue: '',
        projectId: '',
        requestedById: '',
        statusFilter: DEFAULT_FILTERS.status,
        productId: '',
        createdAfter: '',
        createdBefore: '',
        nextPage: null,
      },
      true
    );
  }, [fetchRequests]);

  const buildFetchParams = useCallback(
    (overrides = {}) => ({
      searchValue: searchInput,
      projectId: project,
      requestedById: requestedBy,
      statusFilter: filters.status,
      productId: filters.productId,
      createdAfter: filters.createdAfter,
      createdBefore: filters.createdBefore,
      nextPage: null,
      listMode: listModeRef.current,
      ...overrides,
    }),
    [searchInput, project, requestedBy, filters]
  );

  const debouncedSearch = useCallback(
    value => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      if (value.length >= 2) {
        setRequests([]);
        setIsSearching(true);
      }

      if (isRunnableListSearchQuery(value)) {
        debounceTimeoutRef.current = setTimeout(() => {
          fetchRequests(
            {
              searchValue: value,
              projectId: project,
              requestedById: requestedBy,
              statusFilter: filters.status,
              productId: filters.productId,
              createdAfter: filters.createdAfter,
              createdBefore: filters.createdBefore,
              nextPage: null,
            },
            true
          );
        }, 300);
      } else if (isPartialListSearchQuery(value)) {
        setRequests([]);
        setIsSearching(false);
        resetPaginationForPartialSearch({
          setHasMore,
          hasMoreRef,
          setNextPageUrl,
          abortControllerRef: currentRequestRef,
        });
      }
    },
    [fetchRequests, project, requestedBy, filters]
  );

  const handleSearchInput = value => {
    setSearchInput(value);
    debouncedSearch(value);
  };

  const handleProjectChange = value => {
    setProject(value);
    fetchRequests(buildFetchParams({ projectId: value }), true);
  };

  const handleUserChange = value => {
    setRequestedBy(value);
    fetchRequests(buildFetchParams({ requestedById: value }), true);
  };

  const handleApplyFilters = newFilters => {
    setFilters(prev => {
      const merged = { ...prev, ...newFilters };
      fetchRequests(
        {
          searchValue: searchInput,
          projectId: project,
          requestedById: requestedBy,
          statusFilter: merged.status,
          productId: merged.productId,
          createdAfter: merged.createdAfter,
          createdBefore: merged.createdBefore,
          nextPage: null,
        },
        true
      );
      return merged;
    });
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    fetchRequests(
      {
        searchValue: searchInput,
        projectId: project,
        requestedById: requestedBy,
        statusFilter: DEFAULT_FILTERS.status,
        productId: '',
        createdAfter: '',
        createdBefore: '',
        nextPage: null,
      },
      true
    );
  };

  const toggleFilters = () => setShowFilters(prev => !prev);

  const handleRequestUpdated = () => {
    fetchRequests(buildFetchParams(), true);
  };

  const showList = mode => {
    listModeRef.current = mode;
    setListMode(mode);
    historyNextRef.current = { ...EMPTY_HISTORY_CURSORS };
    fetchRequests(buildFetchParams({ listMode: mode }), true);
  };

  const loadMore = () => {
    if (
      !hasMoreRef.current ||
      isLoading ||
      isPartialListSearchQuery(searchInput)
    ) {
      return;
    }
    if (listModeRef.current === 'history') {
      if (!historyNextRef.current.FULFILLED && !historyNextRef.current.CANCELLED) {
        return;
      }
      fetchRequests(buildFetchParams({ listMode: 'history' }), false);
      return;
    }
    if (!nextPageUrl) {
      return;
    }

    const mismatch = getNextPageUrlFilterMismatch(nextPageUrl, {
      search: searchInput.length >= 2 ? searchInput : '',
      project: project || '',
      requested_by: requestedBy || '',
      status: filters.status || '',
      product_id: filters.productId || '',
    });
    if (mismatch === null) {
      logListSearchError(
        'order-requests',
        'next-page-url-stale-check',
        new Error('Failed to parse nextPageUrl'),
        { nextPageUrl, searchInput, project, requestedBy, filters }
      );
      setNextPageUrl(null);
      setHasMore(false);
      hasMoreRef.current = false;
      return;
    }
    if (mismatch.mismatched) {
      logListSearchEvent('order-requests', 'next-page-url-stale-mismatch', {
        nextPageUrl,
        expected: mismatch.expected,
        actual: mismatch.actual,
      });
      setNextPageUrl(null);
      setHasMore(false);
      hasMoreRef.current = false;
      return;
    }

    fetchRequests(
      {
        searchValue: searchInput,
        projectId: project,
        requestedById: requestedBy,
        statusFilter: filters.status,
        productId: filters.productId,
        createdAfter: filters.createdAfter,
        createdBefore: filters.createdBefore,
        nextPage: nextPageUrl,
      },
      false
    );
  };

  const displayRequests = useMemo(
    () => filterOrderRequestsClientSide(requests, searchInput),
    [requests, searchInput]
  );

  const showEmptyList =
    !isLoading && !isSearching && displayRequests.length === 0;

  return (
    <GlassWorkspaceShell theme="yellow" wide>
      <div className="glass-toolbar">
      <Nav
        fill
        variant="tabs"
        activeKey={listMode}
        onSelect={key => {
          if (key && key !== listMode) showList(key);
        }}
        className="mb-3"
      >
        <Nav.Item>
          <Nav.Link eventKey="open">Open</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="history">History</Nav.Link>
        </Nav.Item>
      </Nav>
      <GenericSearchFilter
        entityType="order-requests"
        placeHolderLabel="Project"
        searchInput={searchInput}
        onSearchInput={handleSearchInput}
        entity={project}
        onEntityChange={handleProjectChange}
        entityOptionsList={projectOptions}
        showFilters={listMode === 'open' && showFilters}
        toggleFilters={listMode === 'open' ? toggleFilters : undefined}
        customActionButton={
          <div
            style={{
              width: '100%',
              minWidth: '200px',
              maxWidth: '420px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <div style={{ minWidth: '220px', flexGrow: 1 }}>
              <DropdownSelect
                optionsList={userSelectList}
                label="Requested by"
                selectedValue={findUserOption(userSelectList, requestedBy)}
                setSelectedValue={opt => handleUserChange(opt?.value ?? '')}
                clearable
              />
            </div>
            {!isViewer ? (
            <Button
              variant="outline-primary"
              className="flex-shrink-0"
              onClick={() => setShowGlobalContactModal(true)}
              style={{ whiteSpace: 'nowrap', minHeight: '40px' }}
            >
              {hasGlobalContactSettings ? 'Lab contact set' : 'Set lab contact'}
            </Button>
            ) : null}
          </div>
        }
        trailingActionButton={
          isViewer || listMode !== 'open' ? null : (
          <OrderRequestAllSuppliersQuoteButton
            requests={displayRequests}
            hasGlobalContact={hasGlobalContactSettings}
            contactDisabledReason={globalContactDisabledReason}
          />
          )
        }
      />

      {listMode === 'open' && showFilters && (
        <OrderRequestFilterComponent
          filters={filters}
          setFilters={setFilters}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
        />
      )}
      </div>

      {(isLoading && requests.length === 0) || isSearching ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
          {isSearching && searchInput.length >= 2 && (
            <div className="mt-2 text-muted">Searching order requests...</div>
          )}
        </div>
      ) : (
            <InfiniteScroll
              pageStart={0}
              loadMore={loadMore}
              hasMore={hasMore && !isLoading}
              threshold={INFINITE_SCROLL_THRESHOLD_PX}
              loader={
                <div className="text-center py-3" key="loader">
                  <Spinner animation="border" size="sm" />
                </div>
              }
            >
              {showEmptyList ? (
                <div className="text-center py-5">
                  <div className="mb-3">
                    <span
                      role="img"
                      aria-label="no results"
                      style={{ fontSize: '3rem' }}
                    >
                      📋
                    </span>
                  </div>
                  <h5 className="text-muted mb-2">No order requests found</h5>
                  <p className="text-muted mb-3">
                    {getOrderRequestsEmptyMessage({
                      searchInput,
                      projectId: project,
                      requestedById: requestedBy,
                      statusFilter: filters.status,
                      productId: filters.productId,
                      createdAfter: filters.createdAfter,
                      createdBefore: filters.createdBefore,
                      listMode,
                    })}
                  </p>
                  {searchInput.length >= 2 && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => {
                        setSearchInput('');
                        fetchRequests(buildFetchParams({ searchValue: '' }), true);
                      }}
                    >
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
                <div className="glass-table-wrap">
                  <OrderRequestsList
                    requests={displayRequests}
                    onRequestUpdated={handleRequestUpdated}
                    hasGlobalContact={hasGlobalContactSettings}
                    globalContactDisabledReason={globalContactDisabledReason}
                    onConfigureContact={() => setShowGlobalContactModal(true)}
                    showSupplierQuotes={listMode === 'open' && !isViewer}
                    showRowActions={listMode === 'open' && !isViewer}
                  />
                </div>
              )}
            </InfiniteScroll>
      )}
      <GlobalOrderContactModal
        open={showGlobalContactModal}
        onClose={() => setShowGlobalContactModal(false)}
        token={token}
        onSettingsUpdated={settings => {
          setGlobalContactSettings(settings);
          showToast('Lab contact saved successfully', 'success', 'top-right', 3000);
        }}
      />
    </GlassWorkspaceShell>
  );
};

export default OrderRequestsPage;
